import {
    getGridBooleanOperators,
    getGridStringOperators,
    GridColDef,
    GridFilterModel,
    GridRowsProp,
} from '@mui/x-data-grid-pro'
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'wouter'
import { normalizeAndSanitize } from '@utils'
import {
    applyOverrides,
    parseFilterOverrides,
    writeFilterModelToUrl,
} from '@utils/gridUrlState'
import API from '@enums/api'
import useDataSource from '@hooks/useDataSource'

export type AllUsersOverview = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rows: any[]
    rowCount: number
    /** The filters this page starts with. The server decides them, see user-overview.ts. */
    defaultFilterModel?: GridFilterModel
}

const NO_DEFAULTS: GridFilterModel = { items: [] }

const filteredOperators = getGridStringOperators().filter(
    (operator) => operator.value !== 'isAnyOf'
)

// Definitions for columns of DataGrid
const columns: GridColDef<GridRowsProp[number]>[] = [
    // These fields are always hidden, but are used for filtering and searching
    {
        field: 'id',
        headerName: 'ID',
        type: 'string',
        width: 0,
        hideable: false,
        filterOperators: filteredOperators,
    },
    {
        field: 'first_name',
        headerName: 'First Name',
        type: 'string',
        width: 0,
        hideable: false,
        sortable: true,
        filterOperators: filteredOperators,
    },
    {
        field: 'last_name',
        headerName: 'Last Name',
        type: 'string',
        width: 0,
        hideable: false,
        filterOperators: filteredOperators,
    },
    // Composite field of `id`, `first_name`, and `last_name` in a compact format
    {
        field: 'nameId',
        filterable: false,
        headerName: 'Name',
        type: 'string',
        width: 150,
        display: 'flex',
        renderCell: (params) => {
            const sanitisedName = normalizeAndSanitize(
                `${params.row['first_name']} ${params.row['last_name']}`
            )

            return (
                <div>
                    <p>{sanitisedName ?? 'Name is only special characters'}</p>
                    <Link
                        href={`/user-deepdive?q=${params.row['id']}`}
                        style={{ fontSize: '0.9em', color: 'darkgray' }}
                    >
                        {params.row['id']}
                    </Link>
                </div>
            )
        },
        renderHeader: () => {
            return (
                <div
                    style={{
                        fontWeight: 'var(--unstable_DataGrid-headWeight)',
                        textAlign: 'left',
                    }}
                >
                    <p>Name</p>
                    <p style={{ fontSize: '0.9em', color: 'darkgray' }}>
                        Account ID
                    </p>
                </div>
            )
        },
        valueGetter: (_, row) => {
            return `${row['first_name']} ${row['last_name']} | ${row['id']}`
        },
        sortComparator: (v1, v2) => {
            return v1 > v2 ? 1 : -1
        },
        filterOperators: filteredOperators,
    },
    // Other fields
    {
        field: 'email',
        headerName: 'Email',
        type: 'string',
        width: 150,
        filterOperators: filteredOperators,
    },
    {
        field: 'account_type',
        headerName: 'Account Type',
        type: 'string',
        width: 150,
        filterOperators: filteredOperators,
    },
    {
        field: 'org_name',
        headerName: 'Organization Name',
        type: 'string',
        width: 150,
        valueGetter: (_, row) => {
            return decodeURI(row['org_name'])
        },
        filterOperators: filteredOperators,
    },
    {
        field: 'created_at',
        headerName: 'Created At',
        width: 150,
        type: 'date',
        valueGetter: (_, row) => {
            return new Date(row.created_at)
        },
        // filterable: false,
    },
    {
        field: 'last_login',
        headerName: 'Last Seen At',
        width: 150,
        type: 'date',
        valueGetter: (_, row) => {
            return new Date(row.last_login)
        },
        // filterable: false,
    },
    {
        field: 'in_mp',
        type: 'boolean',
        headerName: 'Messenger Pigeon',
        width: 150,
        /*
         * The column shows a tick or a cross, so it is filtered as yes or no.
         * It used to offer "is empty" and "is not empty", which describes the
         * id hiding behind the tick rather than the question anyone came here
         * to ask.
         */
        filterOperators: getGridBooleanOperators(),
        valueGetter: (_, row) => {
            return row['in_mp'] ? true : false
        },
        sortComparator: (v1, v2) => {
            return v1 && !v2 ? 1 : -1
        },
    },
]

/**
 * useController hook for UsersOverviewPage
 */
function useController() {
    const [error, setError] = useState<string | null>(null)

    /*
     * Read once, when the page opens. After that the grid is in charge of the
     * filters, and reading the URL again would fight whoever is editing them.
     */
    const [overrides] = useState(parseFilterOverrides)

    /*
     * The defaults come back with the first page of rows, so the grid opens
     * with whatever the URL asked for and picks them up a moment later.
     *
     * While they are empty nothing visibly happens. Once somebody sets a real
     * default, opening the page will cost one extra fetch as the grid applies
     * it. Worth it, because the alternative is a page quietly filtering rows
     * without showing you that it is.
     */
    const [defaultFilterModel, setDefaultFilterModel] =
        useState<GridFilterModel>(NO_DEFAULTS)

    const [filterModel, setFilterModelState] = useState<GridFilterModel>(() =>
        applyOverrides(NO_DEFAULTS, overrides)
    )

    const dataSource = useDataSource({
        getRows: async ({ paginationModel, sortModel, filterModel }) => {
            const res = await API.overviews.allUsers({
                paginationModel,
                sortModel,
                filterModel,
            })

            if (!res.ok) {
                const message = `${res.status} ${res.message}`
                console.error(res.error)
                setError(message)
                throw new Error(message)
            }

            // It worked, so whatever the last error said is no longer true.
            setError(null)

            const defaults = res.value.defaultFilterModel
            if (defaults) {
                setDefaultFilterModel((current) =>
                    JSON.stringify(current) === JSON.stringify(defaults)
                        ? current
                        : defaults
                )
            }

            return {
                rows: res.value.rows,
                rowCount: res.value.rowCount,
            }
        },
    })

    const { setFilterModel } = dataSource

    /*
     * Add the defaults to what is already on screen once they arrive. The URL
     * wins: any column it mentions, or explicitly switches off, is left alone.
     */
    useEffect(() => {
        setFilterModelState((current) => {
            const merged = applyOverrides(defaultFilterModel, {
                ...overrides,
                items: {
                    ...Object.fromEntries(
                        current.items.map((item) => [item.field, item])
                    ),
                    ...overrides.items,
                },
                quickFilterValues: current.quickFilterValues,
                // What is on screen wins: the user may have changed this since
                // the page loaded.
                logicOperator:
                    (current.logicOperator as 'and' | 'or' | undefined) ??
                    overrides.logicOperator,
            })
            return JSON.stringify(merged) === JSON.stringify(current)
                ? current
                : merged
        })
    }, [defaultFilterModel, overrides])

    const onFilterModelChange = useCallback(
        (model: GridFilterModel) => {
            setFilterModelState(model)
            // Keeps the export in step with what is on screen.
            setFilterModel(model)
            writeFilterModelToUrl(model, defaultFilterModel)
        },
        [setFilterModel, defaultFilterModel]
    )

    const dismissError = useCallback(() => setError(null), [])

    return {
        error,
        dismissError,
        columns,
        dataSource,
        filterModel,
        onFilterModelChange,
    }
}

export default useController
